import { createContext, useContext, useEffect, useState } from "react";

/**
 * DataSaver context — controls bandwidth-conscious behaviours:
 *  - Poll intervals (slower when enabled or when the Save-Data client hint is on)
 *  - Autoplay (disabled when enabled)
 *  - Feed page size (smaller when enabled)
 *
 * The context uses:
 *  - navigator.connection.saveData (client-hint from the browser)
 *  - localStorage 'tain-data-saver' user override
 */
const DataSaverContext = createContext({
  enabled: false,
  clientSaveData: false,
  toggle: () => {},
  set: () => {},
});

export function DataSaverProvider({ children }) {
  const [clientSaveData, setClientSaveData] = useState(false);
  const [override, setOverride] = useState(() => {
    if (typeof window === "undefined") return null;
    const v = localStorage.getItem("tain-data-saver");
    return v == null ? null : v === "1";
  });

  useEffect(() => {
    const conn = typeof navigator !== "undefined" && (navigator.connection || navigator.mozConnection || navigator.webkitConnection);
    if (!conn) return;
    const update = () => setClientSaveData(!!conn.saveData);
    update();
    conn.addEventListener?.("change", update);
    return () => conn.removeEventListener?.("change", update);
  }, []);

  const enabled = override != null ? override : clientSaveData;

  const set = (v) => {
    setOverride(v);
    if (v == null) localStorage.removeItem("tain-data-saver");
    else localStorage.setItem("tain-data-saver", v ? "1" : "0");
  };

  const toggle = () => set(!enabled);

  return (
    <DataSaverContext.Provider value={{ enabled, clientSaveData, toggle, set }}>
      {children}
    </DataSaverContext.Provider>
  );
}

export function useDataSaver() {
  return useContext(DataSaverContext);
}

/**
 * Custom hook: interval-with-pause-on-hidden.
 * - Runs the callback every `ms` ms.
 * - Pauses when the tab is hidden (document.hidden).
 * - Resumes and immediately invokes once when the tab becomes visible again.
 * - Applies a 1.5x slowdown when Data Saver is on.
 */
export function useAdaptiveInterval(cb, baseMs) {
  const { enabled } = useDataSaver();
  useEffect(() => {
    let timer = null;
    const ms = Math.round(baseMs * (enabled ? 1.5 : 1.0));
    const start = () => {
      if (timer) return;
      timer = setInterval(() => {
        if (!document.hidden) cb();
      }, ms);
    };
    const stop = () => {
      if (timer) clearInterval(timer);
      timer = null;
    };
    const onVisibility = () => {
      if (document.hidden) return;
      cb();
    };
    start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseMs, enabled]);
}
